import { formatDateTime } from '@/utils';
import { useMutation, useQuery } from '@tanstack/react-query';

import { Badge, Button, Card, CardHeader, ContentLoader } from '@/components/common';
import { getErrorMessage } from '@/services/api';
import { telegramConnectApi, type TelegramConnectStatus } from '@/services/telegramConnect';

const STATUS_KEY = ['messaging', 'connect-link', 'status'] as const;

function readable(data: unknown): data is TelegramConnectStatus {
  const value = data as Partial<TelegramConnectStatus> | undefined;
  return !!value && typeof value.available === 'boolean' && typeof value.connected === 'boolean';
}

function formatWhen(iso: string | null): string {
  if (!iso) return '';
  return formatDateTime(iso);
}

/**
 * "Connect my Telegram" — binds the person's Telegram account to this login in
 * their broker's bot, for when their Telegram number is not the one on their
 * record (sharing their Telegram contact in the bot would never match them).
 *
 * States: loading, a failed fetch (with a retry — never an empty card), an
 * answer we cannot read (shown as unavailable), no bot at the broker, already
 * connected, and not connected (a button that asks the server for a one-time
 * t.me link, shown as a button to open).
 */
export function TelegramConnectCard() {
  const status = useQuery({ queryKey: STATUS_KEY, queryFn: () => telegramConnectApi.getStatus() });
  const link = useMutation({ mutationFn: () => telegramConnectApi.createLink() });
  const data = status.data;
  const ok = readable(data);

  return (
    <Card>
      <CardHeader
        title="Telegram"
        subtitle="Use your broker's Telegram bot as yourself, even if your Telegram number is different"
      />
      {status.isLoading && <ContentLoader />}
      {status.isError && (
        <div role="alert" className="text-sm text-red-700 dark:text-red-300">
          Could not load your Telegram connection.{' '}
          <button
            type="button"
            onClick={() => status.refetch()}
            className="font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
          >
            Try again
          </button>
        </div>
      )}
      {!status.isLoading && !status.isError && !ok && (
        <p className="text-sm text-gray-500 dark:text-gray-400">Your Telegram connection is unavailable right now.</p>
      )}
      {ok && !data.available && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Your broker hasn&apos;t connected a Telegram bot yet, so there is nothing to connect to.
        </p>
      )}
      {ok && data.available && data.connected && (
        <div className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
          <div className="flex items-center gap-2">
            <Badge variant="success">Connected</Badge>
            <span>@{data.bot_username}</span>
          </div>
          {formatWhen(data.connected_at) && (
            <p className="text-xs text-gray-500 dark:text-gray-400">Since {formatWhen(data.connected_at)}</p>
          )}
        </div>
      )}
      {ok && data.available && !data.connected && (
        <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
          {!link.data && (
            <>
              <p>
                Connect your Telegram account to @{data.bot_username} so the bot knows who you are without
                asking for your number.
              </p>
              <Button type="button" onClick={() => link.mutate()} isLoading={link.isPending}>
                Connect my Telegram
              </Button>
            </>
          )}
          {link.data && (
            <>
              <a
                href={link.data.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center rounded-md bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700"
              >
                Open @{link.data.bot_username} in Telegram
              </a>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                This link works once and expires in 10 minutes. Open it on the phone or computer where you use
                Telegram, then come back and refresh this page.
              </p>
            </>
          )}
          {link.isError && (
            <p role="alert" className="text-sm text-red-700 dark:text-red-300">
              {getErrorMessage(link.error)}
            </p>
          )}
        </div>
      )}
    </Card>
  );
}
